<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { uploadFile } from '@/api/file'
import { compressImage } from '@/utils/imageCompress'

const props = defineProps({
  show: Boolean,
  isEdit: Boolean,
  bannerData: Object
})

const emit = defineEmits(['update:show', 'confirm'])

// 表单数据
const form = reactive({
  id: null,
  title: '',
  imageUrl: '',
  position: '',
  sort: 0,
  startTime: '',
  endTime: '',
  remark: '',
  platformConfigs: []
})

// 图片相关
const imageInput = ref()
const pendingImageFile = ref(null) // 待上传的本地文件
const imagePreview = ref('') // 本地预览URL
const showImagePreview = ref(false) // 大图预览弹窗

// 提交 loading
const submitting = ref(false)

// 位置选项
const positionOptions = [
  { label: '首页', value: 'home' },
  { label: '分类页', value: 'category' }
]

// 平台选项
const platformOptions = [
  { label: '微信小程序', value: 1 },
  { label: '支付宝小程序', value: 2 },
  { label: '抖音小程序', value: 3 },
  { label: '快手小程序', value: 4 },
  { label: 'iOS', value: 5 },
  { label: 'Android', value: 6 },
  { label: 'H5', value: 7 }
]

// 跳转类型选项
const linkTypeOptions = [
  { label: '无跳转', value: 0 },
  { label: '应用内页面', value: 1 },
  { label: 'H5页面', value: 2 },
  { label: '资讯详情', value: 3 },
  { label: '产品详情', value: 4 },
  { label: '跳转其他小程序', value: 5 },
  { label: '跳转外部APP', value: 6 },
  { label: '平台特定功能', value: 7 }
]

// 获取平台名称
const getPlatformLabel = (value) => {
  return platformOptions.find(p => p.value === value)?.label || ''
}

// 监听显示/隐藏 初始化表单
watch(() => props.show, (val) => {
  if (val) {
    if (props.isEdit && props.bannerData) {
      Object.assign(form, {
        id: props.bannerData.id,
        title: props.bannerData.title || '',
        imageUrl: props.bannerData.imageUrl || '',
        position: props.bannerData.position || '',
        sort: props.bannerData.sort || 0,
        startTime: props.bannerData.startTime || '',
        endTime: props.bannerData.endTime || '',
        remark: props.bannerData.remark || '',
        platformConfigs: (props.bannerData.platformConfigs || []).map(c => ({
          platform: c.platform,
          linkType: c.linkType || 0,
          linkValue: c.linkValue || '',
          extraParams: c.extraParams || ''
        }))
      })
      pendingImageFile.value = null
      imagePreview.value = ''
    } else {
      resetForm()
    }
  }
})

const resetForm = () => {
  Object.assign(form, {
    id: null,
    title: '',
    imageUrl: '',
    position: '',
    sort: 0,
    startTime: '',
    endTime: '',
    remark: '',
    platformConfigs: []
  })
  pendingImageFile.value = null
  imagePreview.value = ''
}

// 图片上传处理
const triggerImageUpload = () => imageInput.value?.click()

const handleImageChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  // 校验文件类型
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    return
  }

  // 校验文件大小（10MB）
  if (file.size > 10 * 1024 * 1024) {
    ElMessage.warning('图片大小不能超过10MB')
    return
  }

  // 压缩后存到本地，仅预览不上传
  const compressed = await compressImage(file)
  pendingImageFile.value = compressed
  imagePreview.value = URL.createObjectURL(compressed)
  // 清除已有的远程URL，表示需要重新上传
  form.imageUrl = ''
  event.target.value = ''
}

const removeImage = () => {
  form.imageUrl = ''
  pendingImageFile.value = null
  imagePreview.value = ''
}

// 平台配置操作
const addPlatformConfig = () => {
  form.platformConfigs.push({
    platform: null,
    linkType: 0,
    linkValue: '',
    extraParams: ''
  })
}

const removePlatformConfig = (index) => {
  form.platformConfigs.splice(index, 1)
}

// 获取已选中的平台列表（排除当前行）
const getUsedPlatforms = (currentIndex) => {
  return form.platformConfigs
    .filter((_, i) => i !== currentIndex)
    .map(c => c.platform)
    .filter(p => p !== null)
}

// 关闭弹窗
const handleClose = () => {
  emit('update:show', false)
}

// 提交
const handleSubmit = async () => {
  // 校验必填项
  if (!form.imageUrl && !pendingImageFile.value) {
    return ElMessage.warning('请上传Banner图片')
  }
  if (!form.position) {
    return ElMessage.warning('请选择展示位置')
  }
  if (form.platformConfigs.length === 0) {
    return ElMessage.warning('请至少添加一个平台配置')
  }
  for (let i = 0; i < form.platformConfigs.length; i++) {
    if (!form.platformConfigs[i].platform) {
      return ElMessage.warning(`请选择第${i + 1}个平台配置的平台类型`)
    }
  }

  submitting.value = true
  try {
    // 如果有待上传的本地图片，先上传
    let imageUrl = form.imageUrl
    if (pendingImageFile.value) {
      const result = await uploadFile(pendingImageFile.value, 'banner', 'public')
      imageUrl = result.url || result
    }

    const data = {
      title: form.title,
      imageUrl,
      position: form.position,
      sort: form.sort,
      startTime: form.startTime || undefined,
      endTime: form.endTime || undefined,
      remark: form.remark || undefined,
      platformConfigs: form.platformConfigs.map(c => ({
        platform: c.platform,
        linkType: c.linkType,
        linkValue: c.linkValue || undefined,
        extraParams: c.extraParams || undefined
      }))
    }
    if (props.isEdit) {
      data.id = form.id
    }
    emit('confirm', data)
  } catch (error) {
    ElMessage.error('图片上传失败')
  } finally {
    submitting.value = false
  }
}

const done = () => {
  submitting.value = false
  handleClose()
}

defineExpose({ done })
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col">
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 class="text-lg font-semibold text-slate-900">
                {{ isEdit ? '编辑Banner' : '新增Banner' }}
              </h3>
              <button
                @click="handleClose"
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 内容区 -->
            <div class="flex-1 overflow-y-auto p-6">
              <div class="space-y-6">
                <!-- 基本信息 -->
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 mb-4">基本信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <!-- Banner标题 -->
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">Banner标题</label>
                      <input
                        v-model="form.title"
                        type="text"
                        placeholder="请输入Banner标题"
                        class="form-input w-full"
                      />
                    </div>

                    <!-- 展示位置 -->
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">
                        展示位置 <span class="text-red-500">*</span>
                      </label>
                      <el-select
                        v-model="form.position"
                        placeholder="请选择展示位置"
                        class="form-select w-full"
                      >
                        <el-option v-for="opt in positionOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
                      </el-select>
                    </div>

                    <!-- 排序值 -->
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">排序值</label>
                      <input
                        v-model.number="form.sort"
                        type="number"
                        min="0"
                        placeholder="值越小越靠前"
                        class="form-input w-full"
                      />
                    </div>

                    <!-- 占位 -->
                    <div></div>

                    <!-- 生效开始时间 -->
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">生效开始时间</label>
                      <input
                        v-model="form.startTime"
                        type="datetime-local"
                        class="form-input w-full"
                      />
                    </div>

                    <!-- 生效结束时间 -->
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">生效结束时间</label>
                      <input
                        v-model="form.endTime"
                        type="datetime-local"
                        class="form-input w-full"
                      />
                    </div>

                    <!-- 备注（跨两列） -->
                    <div class="col-span-2">
                      <label class="block text-sm font-medium text-slate-700 mb-1">备注</label>
                      <textarea
                        v-model="form.remark"
                        rows="2"
                        placeholder="请输入备注信息"
                        class="form-textarea"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <!-- Banner图片 -->
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 mb-4">
                    Banner图片 <span class="text-red-500">*</span>
                  </h4>
                  <div v-if="form.imageUrl || imagePreview" class="relative group inline-block">
                    <img
                      :src="form.imageUrl || imagePreview"
                      alt="Banner预览"
                      class="h-36 rounded-lg border border-slate-200 object-cover"
                    />
                    <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
                      <button
                        @click="showImagePreview = true"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40 cursor-pointer"
                        title="查看大图"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
                      </button>
                      <button
                        @click="triggerImageUpload"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40 cursor-pointer"
                        title="更换图片"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      </button>
                      <button
                        @click="removeImage"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-red-500/80 cursor-pointer"
                        title="删除图片"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                  <div
                    v-else
                    @click="triggerImageUpload"
                    class="w-full h-36 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center hover:border-primary-400 hover:bg-primary-50/30 transition-colors cursor-pointer"
                  >
                    <svg class="w-8 h-8 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    <span class="text-sm text-slate-500">点击选择Banner图片</span>
                    <span class="text-xs text-slate-400 mt-1">建议尺寸 750x340，支持JPG/PNG，不超过10MB</span>
                  </div>
                  <input
                    ref="imageInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleImageChange"
                  />
                </div>

                <!-- 平台跳转配置 -->
                <div>
                  <div class="flex items-center justify-between mb-4">
                    <h4 class="text-sm font-semibold text-slate-900">
                      平台跳转配置 <span class="text-red-500">*</span>
                    </h4>
                    <button
                      @click="addPlatformConfig"
                      :disabled="form.platformConfigs.length >= 7"
                      class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                      添加平台
                    </button>
                  </div>

                  <div v-if="form.platformConfigs.length === 0" class="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
                    暂无平台配置，请点击上方按钮添加
                  </div>

                  <div v-else class="space-y-3">
                    <div
                      v-for="(config, index) in form.platformConfigs"
                      :key="index"
                      class="bg-slate-50 rounded-lg p-4 border border-slate-200"
                    >
                      <div class="flex items-center justify-between mb-3">
                        <span class="text-xs font-medium text-slate-500">
                          平台配置 #{{ index + 1 }}
                          <template v-if="config.platform">
                            - {{ getPlatformLabel(config.platform) }}
                          </template>
                        </span>
                        <button
                          @click="removePlatformConfig(index)"
                          class="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <!-- 平台类型 -->
                        <div>
                          <label class="block text-xs font-medium text-slate-600 mb-1">
                            平台类型 <span class="text-red-500">*</span>
                          </label>
                          <el-select
                            v-model="config.platform"
                            placeholder="请选择平台"
                            class="form-select w-full"
                          >
                            <el-option
                              v-for="opt in platformOptions"
                              :key="opt.value"
                              :label="opt.label"
                              :value="opt.value"
                              :disabled="getUsedPlatforms(index).includes(opt.value)"
                            />
                          </el-select>
                        </div>

                        <!-- 跳转类型 -->
                        <div>
                          <label class="block text-xs font-medium text-slate-600 mb-1">跳转类型</label>
                          <el-select
                            v-model="config.linkType"
                            class="form-select w-full"
                          >
                            <el-option v-for="opt in linkTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
                          </el-select>
                        </div>

                        <!-- 跳转目标值 -->
                        <div>
                          <label class="block text-xs font-medium text-slate-600 mb-1">跳转目标值</label>
                          <input
                            v-model="config.linkValue"
                            type="text"
                            placeholder="页面路径/URL/目标ID"
                            :disabled="config.linkType === 0"
                            class="form-input w-full disabled:cursor-not-allowed"
                          />
                        </div>

                        <!-- 扩展参数 -->
                        <div>
                          <label class="block text-xs font-medium text-slate-600 mb-1">扩展参数</label>
                          <input
                            v-model="config.extraParams"
                            type="text"
                            placeholder='JSON格式，如{"appId":"wx123"}'
                            :disabled="config.linkType === 0"
                            class="form-input w-full disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 底部按钮 -->
            <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
              <button
                @click="handleClose"
                class="btn-secondary"
              >
                取消
              </button>
              <button
                @click="handleSubmit"
                :disabled="submitting"
                class="btn-primary"
              >
                {{ submitting ? '提交中...' : '确定' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 大图预览 -->
    <Transition name="modal">
      <div v-if="showImagePreview" class="fixed inset-0 z-[60] flex items-center justify-center" @click.self="showImagePreview = false">
        <div class="absolute inset-0 bg-black/70" @click="showImagePreview = false"></div>
        <div class="relative max-w-[90vw] max-h-[90vh]">
          <img :src="form.imageUrl || imagePreview" alt="Banner大图" class="max-w-full max-h-[85vh] rounded-lg object-contain" />
          <button
            @click="showImagePreview = false"
            class="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
